/**
 * Point Load Test — grid option lists, defaults, and column metadata.
 * Column configs are data objects (CODING-STANDARDS), consumed by PLTBatchGrid.
 *
 * SYNC-POINTER: option lists / codes mirror backend/src/constants/pointLoadTest.ts.
 */
import type { PltInputFields } from '../../../types/pointLoadTest';
import { PLT_K_FACTOR_DEFAULT, PLT_LOAD_AREA_DEFAULT_M2 } from './computations';

export { PLT_K_FACTOR_DEFAULT, PLT_LOAD_AREA_DEFAULT_M2 };

export const PLT_TEST_TYPE_OPTIONS = [
  { value: 'D', label: 'Diametral (D)' },
  { value: 'A', label: 'Axial (A)' },
  { value: 'B', label: 'Block (B)' },
  { value: 'L', label: 'Irregular Lump (L)' },
];

export const PLT_DIRECTION_OPTIONS = [
  { value: 'U', label: 'Unknown / Random (U)' },
  { value: '//', label: 'Parallel (//)' },
  { value: 'P', label: 'Perpendicular (P)' },
];

export const PLT_VALIDITY_SELECT_OPTIONS = [
  { value: 'Valid - Unknown', label: 'Valid – Unknown' },
  { value: 'Valid - Intact Rock', label: 'Valid – Intact Rock' },
  { value: 'Valid - Rock Fabric', label: 'Valid – Rock Fabric' },
  { value: 'Valid - Microdefect', label: 'Valid – Microdefect' },
  { value: 'Invalid - Chipping', label: 'Invalid – Chipping' },
  { value: 'Invalid - Punching', label: 'Invalid – Punching' },
];

/** A fresh, empty input record (program defaults pre-applied). */
export function emptyPltInput(): PltInputFields {
  return {
    testType: 'A',
    loadDirection: 'U',
    validity: 'Valid - Unknown',
    unit: null,
    gaugePsi: null,
    loadAreaM2: PLT_LOAD_AREA_DEFAULT_M2,
    widthMm: null,
    platenDistanceMm: null,
    platenDistanceFailureMm: null,
    sampleLengthMm: null,
    kFactor: PLT_K_FACTOR_DEFAULT,
    notes: null,
    additionalObservations: null,
  };
}

/** Numeric input columns (editable). decimals drives the display rounding.
 *  `required` fields get a subtle empty-state cue so it's clear what still
 *  needs entry; D' is optional ("-"), and Load Area / K ship with defaults. */
export interface PltNumColumn {
  field: keyof PltInputFields;
  label: string;
  unit?: string;
  decimals: number;
  width: number;
  required?: boolean;
}

export const PLT_INPUT_NUM_COLUMNS: PltNumColumn[] = [
  { field: 'gaugePsi', label: 'Gauge', unit: 'PSI', decimals: 0, width: 84, required: true },
  { field: 'widthMm', label: 'Width W', unit: 'mm', decimals: 1, width: 84, required: true },
  { field: 'platenDistanceMm', label: 'Dis. D', unit: 'mm', decimals: 1, width: 84, required: true },
  { field: 'platenDistanceFailureMm', label: "D' at fail.", unit: 'mm', decimals: 1, width: 84 },
  // BUG-2026-0150 — template col R "Sample Length (L), mm". Record-only
  // (no calc); was stored but had no entry cell.
  { field: 'sampleLengthMm', label: 'Length L', unit: 'mm', decimals: 1, width: 84 },
  { field: 'loadAreaM2', label: 'Load Area', unit: 'm²', decimals: 4, width: 90 },
  { field: 'kFactor', label: 'K', unit: '', decimals: 0, width: 64 },
];

/** Read-only computed result columns. */
export interface PltResultColumn {
  field: string;            // key on PltComputedFields
  label: string;
  unit?: string;
  decimals: number;
  width: number;
}

export const PLT_RESULT_COLUMNS: PltResultColumn[] = [
  // BUG-2026-0150 — template col M "GAUGE (kPa)" (= PSI × 6.89476); was
  // computed all along but never shown as its own column.
  { field: 'gaugeKpa', label: 'Gauge', unit: 'kPa', decimals: 1, width: 76 },
  { field: 'failureLoadKn', label: 'Load P', unit: 'kN', decimals: 2, width: 72 },
  { field: 'deMm', label: 'De', unit: 'mm', decimals: 2, width: 64 },
  { field: 'de2Mm2', label: 'De²', unit: 'mm²', decimals: 1, width: 72 },
  { field: 'isMpa', label: 'Is', unit: 'MPa', decimals: 2, width: 64 },
  { field: 'sizeCorrectionF', label: 'F', unit: '', decimals: 3, width: 60 },
  { field: 'is50Mpa', label: 'Is(50)', unit: 'MPa', decimals: 2, width: 72 },
  { field: 'ucsMpa', label: 'UCS', unit: 'MPa', decimals: 1, width: 72 },
];
